import { formatCurrency, formatDate } from 'ddc-intl';
import { isBrowser } from 'ws-scripts/modules/environment';

export const isNil = (value) => {
	return value === null || value === undefined;
};

export const isEmpty = (obj = {}) => {
	if (!obj) {
		return true;
	}
	const keys = Object.keys(obj);
	return keys.length === 0 || keys.every((key) => obj[key] === undefined);
};

export const processOfferData = (
	offer,
	{ locale, uuid, inventoryVehiclePhoto, vdpLink }
) => {
	return {
		uuid,
		vdpLink,
		customer: {
			firstName: offer.customer?.firstName,
			lastName: offer.customer?.lastName,
			email: offer.customer?.email,
			phone: offer.customer?.phone
		},
		trade: {
			vehicle: {
				year: offer.tradeVehicle?.year,
				make: offer.tradeVehicle?.make,
				model: offer.tradeVehicle?.model,
				trim: offer.tradeVehicle?.trim,
				estimatedEquity: offer.tradeVehicle?.estimatedEquity,
				equityConsumptionEnabled:
					offer.scenario?.equityConsumptionEnabled,
				canTradeVehicle: !isEmpty(offer.tradeVehicle || {})
			}
		},
		offer: {
			offerId: offer.offerId,
			vehicle: {
				chromeStyleId: offer.inventoryVehicle?.chromeStyleId,
				year: offer.inventoryVehicle?.year,
				make: offer.inventoryVehicle?.make,
				model: offer.inventoryVehicle?.model,
				trim: offer.inventoryVehicle?.trim,
				bodyStyle: offer.inventoryVehicle?.bodyStyle,
				type: offer.inventoryVehicle?.inventoryType,
				price: offer.scenario?.salePrice,
				vin: offer.inventoryVehicle?.vin,
				inventoryVehiclePhoto
			},
			payment: {
				monthly: offer.scenario?.estimatedMonthlyPayment,
				rate: `${offer.scenario?.buyRate || 0}${
					offer.scenario?.rateType || ''
				}`,
				term: offer.scenario?.contractTerm
			},
			savings:
				(offer.customer?.currentFinancing?.monthlyPayment || 0) -
				(offer.scenario?.estimatedMonthlyPayment || 0)
		},
		disclaimer: {
			loanCreditScore: offer.scenario?.creditScore,
			leaseCreditScore: offer.scenario?.creditScore,
			APR: offer.scenario?.buyRate,
			term: offer.scenario?.contractTerm,
			leaseTerm: offer.scenario?.contractTerm,
			monthlyPaymentPerThousand: !isNil(
				offer.scenario?.monthlyPaymentPerThousand
			)
				? formatCurrency(
						offer.scenario?.monthlyPaymentPerThousand,
						locale
				  )
				: null,
			downPayment: !isNil(offer.scenario?.downPayment)
				? formatCurrency(offer.scenario?.downPayment, locale)
				: null,
			lenderLegalName: offer.scenario?.lenderLegalName,
			leaseLenderLegalName: offer.scenario?.lenderLegalName,
			loanProgramEndDate: !isNil(offer.scenario?.expirationDate)
				? formatDate(offer.scenario?.expirationDate, locale, {
						year: 'numeric',
						month: 'long',
						day: 'numeric'
				  })
				: null,
			leaseProgramEndDate: !isNil(offer.scenario?.expirationDate)
				? formatDate(offer.scenario?.expirationDate, locale, {
						year: 'numeric',
						month: 'long',
						day: 'numeric'
				  })
				: null,
			dealershipName: offer.customer?.dealerName,
			offerType: offer.scenario?.offerType,
			leaseDueAtSigning: !isNil(offer.scenario?.leaseDueAtSigning)
				? formatCurrency(offer.scenario.leaseDueAtSigning, locale)
				: null,
			acquisitionFee: !isNil(offer.scenario?.acquisitionFee)
				? formatCurrency(offer.scenario?.acquisitionFee, locale)
				: null,
			capCostTotal: !isNil(offer.scenario?.capCostTotal)
				? formatCurrency(offer.scenario?.capCostTotal, locale)
				: null,
			capCostReduction: !isNil(offer.scenario?.capCostReduction)
				? formatCurrency(offer.scenario.capCostReduction, locale)
				: null,
			leaseSecurityDeposit: !isNil(offer.scenario?.securityDeposit)
				? formatCurrency(offer.scenario?.securityDeposit, locale)
				: null,
			monthlyPayment: !isNil(offer.scenario?.estimatedMonthlyPayment)
				? formatCurrency(
						offer.scenario?.estimatedMonthlyPayment,
						locale
				  )
				: null,
			totalMonthlyPayments:
				!isNil(offer.scenario?.contractTerm) &&
				!isNil(offer.scenario?.estimatedMonthlyPayment)
					? formatCurrency(
							offer.scenario?.contractTerm *
								offer.scenario?.estimatedMonthlyPayment,
							locale
					  )
					: null,
			excessMileCharge: !isNil(offer.scenario?.excessMileageCharge)
				? formatCurrency(
						offer.scenario?.excessMileageCharge,
						locale,
						true
				  )
				: null,
			annualMiles:
				!isNil(offer.scenario?.totalMiles) &&
				!isNil(offer.scenario?.contractTerm)
					? offer.scenario?.totalMiles /
					  (offer.scenario?.contractTerm / 12)
					: null
		}
	};
};

export const newrelicAction = (action, facets) => {
	if (window && window.newrelic && typeof window.newrelic === 'object') {
		window.newrelic.addPageAction(action, facets);
	}
};

export const trackErrorEvent = (eventDetails) => {
	const defaultDetails = {
		event: 'widgetSystemEvent',
		nonInteractive: true,
		result: 'ws-amp-offer container error message shown',
		value: undefined
	};
	const combinedDetails = Object.assign(defaultDetails, eventDetails);
	if (isBrowser) {
		const DDC = window.DDC || {};
		DDC.pushToGtmDataLayer(combinedDetails);
	}
};

export const buildContactUsUrl = (
	formUrl,
	customer,
	{ offerId, itemId } = {}
) => {
	let query = `${formUrl}?`;
	if (itemId) {
		query = `${query}itemId=${itemId}&`;
	}
	if (offerId) {
		query = `${query}offerId=${offerId}&`;
	}
	const contact = ['firstName', 'lastName', 'email', 'phone'];
	query = contact.reduce((acc, currKey) => {
		if (!customer[currKey]) {
			return acc;
		}
		return `${acc}contact.${currKey}=${customer[currKey]}&`;
	}, query);
	query = query.replace(/&$/, '');
	query = encodeURI(query);
	return query;
};

export const mockVDPLinkData = {
	href: '/exotic-new/Hyundai/2021-Hyundai-Sonata-93cc35960a0e0adf6a6e7a172d96c68e.htm',
	uuid: '93cc35960a0e0adf6a6e7a172d96c68e',
	isLoading: false
};

export const getTradeLabel = ({ includeTrade, estimatedEquity }) => {
	const isPositiveEquity = estimatedEquity >= 0;
	const isNegativeEquity = estimatedEquity < 0;

	if (includeTrade && isPositiveEquity) {
		return 'INCLUDES_TRADE_POSITIVE_EQUITY';
	} else if (includeTrade && isNegativeEquity) {
		return 'INCLUDES_TRADE_NEGATIVE_EQUITY';
	} else if (!includeTrade && isPositiveEquity) {
		return 'EXCLUDES_TRADE_POSITIVE_EQUITY';
	} else if (!includeTrade && isNegativeEquity) {
		return 'EXCLUDES_TRADE_NEGATIVE_EQUITY';
	}

	return 'NO_TRADE_IN_VALUE_END';
};

export const getVehicleDataString = (year, model, trim) => {
	const vehicleString = [year, model, trim]
		.filter((prop) => !!prop)
		.join(' ');
	return vehicleString;
};
